import { useState } from 'react';
import { toast } from 'sonner';
import type { StoredFileDetail } from '../../domain/entities/stored-file.entity';
import { useFileViewUrl } from '../hooks/use-stored-files';

/**
 * Abre o conteúdo do arquivo. Imagem aparece aqui mesmo; o resto vai para uma
 * aba nova, onde o navegador mostra (PDF) ou baixa. A URL só é pedida no
 * clique, porque cada pedido fica no audit do back como "admin abriu".
 *
 * O `alt` e o rótulo não levam o nome do arquivo: ele pode identificar a pessoa
 * (Decisão 7 do épico files-admin, ver `stored-file.entity.ts`).
 */
export function FilePreviewCard({ file }: { file: StoredFileDetail }) {
  const isImage = file.mime.startsWith('image/');
  const viewUrl = useFileViewUrl(file.fileId);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const open = () => {
    if (isImage) {
      viewUrl.mutate(undefined, { onSuccess: setImageUrl });
      return;
    }
    // A aba abre no clique, antes do await: aberta depois, o bloqueador de
    // pop-up a recusa. Sem `noopener` aqui porque com ele o `open` devolve null.
    const tab = window.open('', '_blank');
    // Bloqueada, não pede a URL: navegar na própria aba tiraria o admin do
    // detalhe, e o back auditaria uma olhada que não aconteceu.
    if (!tab) {
      toast.error(
        'O navegador bloqueou a nova aba. Permita pop-ups para este site.',
      );
      return;
    }
    tab.opener = null;
    viewUrl.mutate(undefined, {
      onSuccess: (url) => {
        tab.location.href = url;
      },
      onError: () => tab.close(),
    });
  };

  if (imageUrl) {
    return (
      <div className="border-(--color-border) bg-(--color-bg-primary) space-y-3 rounded-xl border p-4">
        <img
          src={imageUrl}
          alt="Pré-visualização do arquivo"
          // URL expirada ou objeto fora do ar: volta ao botão, que pede outra.
          onError={() => {
            setImageUrl(null);
            toast.error(
              'Não foi possível carregar a imagem. Tente abrir de novo.',
            );
          }}
          className="mx-auto max-h-[70vh] rounded-lg object-contain"
        />
        <div className="flex justify-end gap-4 text-sm">
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-(--color-accent) hover:underline"
          >
            Abrir em nova aba
          </a>
          <button
            type="button"
            onClick={() => setImageUrl(null)}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary)"
          >
            Fechar pré-visualização
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={viewUrl.isPending}
      aria-busy={viewUrl.isPending}
      className="border-(--color-border) bg-(--color-bg-primary) hover:border-(--color-text-secondary) flex w-full items-center justify-between gap-4 rounded-xl border p-4 text-left transition-colors disabled:opacity-60"
    >
      <span>
        <span className="text-(--color-text-primary) block text-sm font-medium">
          {isImage ? 'Ver imagem' : 'Abrir arquivo em nova aba'}
        </span>
        <span className="text-(--color-text-secondary) block text-xs">
          {viewUrl.isPending ? 'Gerando link…' : file.mime}
        </span>
      </span>
      <span aria-hidden="true" className="text-(--color-text-secondary)">
        {isImage ? '⤢' : '↗'}
      </span>
    </button>
  );
}
